import { computed, Directive, inject, input } from '@angular/core';

import { SchemaActionsHost } from '../action/schema-action';
import { SCHEMA_STATE } from '../address/schema-state';
import { connectSchemaDataSource, SchemaDataSourceHost } from '../data-source/schema-data-source';
import { ComponentNode } from '../model/component-node';
import { DataState, idleState } from '../model/data-state';
import { projectState } from '../model/project-state';
import { SchemaNode } from '../model/schema-node.types';
import { deriveSchemaViewState } from '../model/schema-view-state';
import { declareSchemaModals } from '../modal/schema-modal-host';
import { SchemaQueryHost } from '../query/schema-query';
import { resolveSchemaComponent } from './resolve-schema-component';

/**
 * A node, once its outlet has let it exist: the hosts it opens for its subtree, the source it connects,
 * the actions and query it declares, and the component it resolves to.
 *
 * Internal to `kros-schema-node-outlet`, which creates it under the node's `showIf` — so a hidden node has
 * none of this, and one whose condition stops holding is destroyed with its source. It sits on an
 * `ng-container`, the way `[formGroup]` can, so it adds no element: the component it resolves renders
 * beneath it through `*ngComponentOutlet` and sees its hosts, while the outlet's own injector, above it,
 * does not — which is what keeps the node's `showIf` reading the source *above* the node.
 *
 * Every node is a potential host: a node whose `dataSource` names a `type` connects that source here, and
 * everything rendered below resolves `injectSchemaDataSource()` to it. A node without one provides a host
 * that passes through, so it hides nothing from its subtree.
 *
 * `SchemaActionsHost` is here for the same reason, one level up: it merges this node's `actions` over the
 * ones declared above, so a control anywhere below can name one. It also holds this node's `actionsMap`,
 * which is *not* merged — it re-points the slots of the component rendered right here.
 *
 * `SchemaQueryHost` completes the set: it resolves this node's `query` states over the ones declared
 * above, so a sort control, a filter sheet and the data source building the request all read the same
 * state without any of them naming a storage mechanism.
 *
 * It provides `SCHEMA_STATE` too — the node's state, which an action fired below it reads — and hands its
 * own source the state the node was handed, which that source's config reads.
 *
 * And it hands the node's `modals` to the app's modal host with its own injector, so a declared modal renders under
 * this node — and a node its `showIf` hides declares none. An entry's own `showIf` is decided here, on the state this
 * node hands down, the way a child's outlet would decide it.
 */
@Directive({
  selector: '[krosSchemaNodeScope]',
  exportAs: 'krosSchemaNodeScope',
  providers: [
    SchemaDataSourceHost,
    SchemaActionsHost,
    SchemaQueryHost,
    /**
     * What an action fired below this node reads as `$data` and `$state`: this node's state, the one its component
     * renders (D11). A component binds its own `state` input instead, so this reaches only what has no input to bind.
     */
    { provide: SCHEMA_STATE, useFactory: () => inject(SchemaNodeScopeDirective).effectiveState },
  ],
})
export class SchemaNodeScopeDirective {
  readonly node = input<SchemaNode | undefined>(undefined, { alias: 'krosSchemaNodeScope' });

  /** The state inherited from the parent. A node that declares its own `dataSource` overrides it. */
  readonly state = input<DataState>();

  readonly #host = inject(SchemaDataSourceHost);

  readonly #actionsHost = inject(SchemaActionsHost);

  readonly #queryHost = inject(SchemaQueryHost);

  readonly #dataSource = computed(() => this.node()?.dataSource);

  readonly #producerType = computed(() => this.#dataSource()?.type);

  /**
   * `type` produces — a source of the node's own. `key` projects a slice of what the parent handed
   * down. Neither means the node renders on the parent's state unchanged, which is what every call
   * site binding `[state]` relies on.
   */
  readonly effectiveState = computed<DataState | undefined>(() => {
    const dataSource = this.#dataSource();

    if (dataSource?.type) {
      return this.#host.dataSource()?.state() ?? idleState();
    }

    if (dataSource?.key) {
      return projectState(this.state() ?? idleState(), dataSource.key);
    }

    return this.state();
  });

  readonly #effectiveSlot = computed<ComponentNode | undefined>(() => {
    const node = this.node();
    if (!node) {
      return undefined;
    }
    const viewState = deriveSchemaViewState(this.effectiveState());
    const stateSlot = viewState === 'content' ? undefined : node.states?.[viewState];
    return stateSlot ?? { componentType: node.componentType, preset: node.preset, config: node.config };
  });

  readonly effectiveConfig = computed(() => this.#effectiveSlot()?.config);

  readonly #effectiveComponentType = computed(() => this.#effectiveSlot()?.componentType);

  /** The preset of the node actually being resolved: a state replacement names its own or resolves from the default bucket. */
  readonly #effectivePreset = computed(() => this.#effectiveSlot()?.preset);

  readonly component = resolveSchemaComponent(this.#effectiveComponentType, this.#effectivePreset);

  constructor() {
    connectSchemaDataSource(this.#producerType, this.#dataSource, this.state);

    this.#actionsHost.registerDeclarations(computed(() => this.node()?.actions));

    this.#actionsHost.registerMap(computed(() => this.node()?.actionsMap));

    this.#queryHost.registerQuery(computed(() => this.node()?.query));

    declareSchemaModals(
      computed(() => this.node()?.modals),
      this.effectiveState
    );
  }
}
